import { useMemo } from "react";

import { useCatalogueContext } from "../../../contexts/CatalogueContext/CatalogueContext";
import { useConfigContext } from "../../../contexts/ConfigContext/ConfigContext";
import { ICategoryList } from "../types";

export const useCatalogueData = () => {
	const {
		state: { products, isLoading },
	} = useCatalogueContext((state) => state);
	const { state: hiddenAlacarteIDs } = useConfigContext(
		(state) => state.config?.hidden_alacarte_bmi
	);

	const filteredProducts = useMemo(() => {
		if (!products) {
			return [];
		}
		if (!hiddenAlacarteIDs || !Array.isArray(hiddenAlacarteIDs)) {
			return products;
		}

		return products.filter((product) => !hiddenAlacarteIDs.includes(product.brand_menu_item_id));
	}, [products, hiddenAlacarteIDs]);

	const categories = useMemo<ICategoryList[]>(() => {
		if (!filteredProducts) {
			return [];
		}

		const categoriesMap = filteredProducts?.reduce<
			Map<string, ICategoryList & { displayOrder: number }>
		>((acc, product) => {
			const categoryId = product.food_supplier_category?.brand_category_id?.toString();

			if (categoryId && !acc.has(categoryId)) {
				acc.set(categoryId, {
					id: categoryId,
					name: product.food_supplier_category.title ?? "",
					name_cn: product.food_supplier_category.title_cn ?? "",
					description: product.food_supplier_category.description ?? "",
					description_cn: product.food_supplier_category.description_cn ?? "",
					display_order: product.food_supplier_category.display_order ?? 0,
					product_image: product.food_supplier_category.product_image ?? "",
					product_thumbnail: product.food_supplier_category.product_thumbnail ?? "",
					displayOrder: product.food_supplier_category.display_order ?? 0,
				});
			}

			return acc;
		}, new Map());

		// Convert map to array and sort by display_order
		return Array.from(categoriesMap.values())
			.sort((a, b) => a.displayOrder - b.displayOrder)
			.map(({ displayOrder, ...category }) => category);
	}, [filteredProducts]);

	const sortedProducts = useMemo(() => {
		if (!filteredProducts) {
			return filteredProducts;
		}

		return [...filteredProducts].sort((a, b) => a.display_order - b.display_order);
	}, [filteredProducts]);

	return { categories, products: sortedProducts, isLoading };
};
